/**
 * Whereby Integration Handlers
 *
 * Handlers for Whereby meeting creation and management
 */

import { handleRequest } from './config'
import { State } from '../../hooks/useAppState'
import { loadCloudFunctionsHandler } from '../handlers'
import {
  ClaimWherebyCallRequest,
  ClaimWherebyCallResponse,
  GetLiveWherebyRoomsResponse,
  ReleaseWherebyCallRequest,
  SprintActivityWherebyMeetingRequest,
  SprintCallRoom,
  WherebyMeeting
} from '../../../../pure-js/types/WherebyTypes'
import { ActivityType } from '../../../../pure-js/enums/ActivityType'

const mockSprintCallRoom = (room: WherebyMeeting | null): SprintCallRoom => ({
  room,
  sprintTitle: 'Mock Sprint Alpha',
  activityType: ActivityType.INTRODUCTION,
  activityTitle: 'Kickoff',
  teamId: 'mock-team-alpha'
})

// ========================================
// WHEREBY HANDLERS
// ========================================

/**
 * Create a Whereby meeting
 * Used for video conferencing in team sessions
 */
export const createWherebyMeeting = async (
  meetingData: { roomNamePrefix?: string; endDate?: string; teamId: string },
  state: State
) => {
  return handleRequest(
    () => Promise.resolve({ data: { roomUrl: 'https://mock.whereby.com/room', meetingId: 'mock-id' }, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.createWherebyMeeting(meetingData, state)
    }
  )
}

/**
 * Read the active activity-scoped Whereby room from the whereby_meetings table (no create).
 */
export const getActivityWherebyMeeting = async (params: { teamId: string; activityId: string }, state: State) => {
  return handleRequest(
    () => Promise.resolve({ data: null as WherebyMeeting | null, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getActivityWherebyMeeting(params, state)
    }
  )
}

/**
 * Create or reuse the activity-scoped Whereby room (server resolves the window + session).
 */
export const ensureActivityWherebyMeeting = async (
  params: { teamId: string; sprintId: string; activityId: string },
  state: State
) => {
  return handleRequest(
    () => Promise.resolve({ data: { roomUrl: 'https://mock.whereby.com/activity' } as WherebyMeeting, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.ensureActivityWherebyMeeting(params, state)
    }
  )
}

/**
 * Read the active room a whole sprint holds one activity in (no create).
 */
export const getSprintActivityWherebyMeeting = async (params: SprintActivityWherebyMeetingRequest, state: State) => {
  return handleRequest(
    () => Promise.resolve({ data: mockSprintCallRoom(null) as SprintCallRoom | null, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getSprintActivityWherebyMeeting(params, state)
    }
  )
}

/**
 * Create or reuse the room a whole sprint holds one activity in.
 */
export const ensureSprintActivityWherebyMeeting = async (params: SprintActivityWherebyMeetingRequest, state: State) => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: mockSprintCallRoom({
          roomUrl: 'https://mock.whereby.com/sprint'
        } as WherebyMeeting) as SprintCallRoom | null,
        success: true
      }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.ensureSprintActivityWherebyMeeting(params, state)
    }
  )
}

/**
 * Read the team's current active Whereby room from the whereby_meetings table (no create).
 */
export const getTeamWherebyMeeting = async (teamId: string, state: State) => {
  return handleRequest(
    () => Promise.resolve({ data: null as WherebyMeeting | null, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getTeamWherebyMeeting({ teamId }, state)
    }
  )
}

/**
 * Take (or refresh) this user's single allowed Whereby connection. Returns 'blocked' with the connection
 * that holds it when another live tab/device of the same user has the call.
 */
export const claimWherebyCall = async (req: ClaimWherebyCallRequest, state: State) => {
  return handleRequest(
    () => Promise.resolve({ data: { status: 'granted' } as ClaimWherebyCallResponse | null, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.claimWherebyCall(req, state)
    }
  )
}

/**
 * Give up this user's call slot (no-op unless this client still holds it).
 */
export const releaseWherebyCall = async (req: ReleaseWherebyCallRequest, state: State) => {
  return handleRequest(
    () => Promise.resolve({ data: null, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.releaseWherebyCall(req, state)
    }
  )
}

/**
 * List currently-live Whereby rooms (presence). Powers the backoffice Live Rooms monitor
 * and the facilitator Control Tower presence view.
 */
export const getLiveWherebyRooms = async (state: State) => {
  return handleRequest(
    () => Promise.resolve({ data: [] as GetLiveWherebyRoomsResponse, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getLiveWherebyRooms(state)
    }
  )
}
